import React, { useEffect, useMemo, useState } from 'react';
import useFleetGoogleMaps from '../utils/useFleetGoogleMaps';
import './DeviceDetails.css';
import { toast } from 'react-toastify';
import FilterDropdown from './FilterDropdown';

const toStampUTC = (dt) => {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  const hh = String(dt.getUTCHours()).padStart(2, '0');
  const mm = String(dt.getUTCMinutes()).padStart(2, '0');
  const ss = String(dt.getUTCSeconds()).padStart(2, '0');
  return `${y}${m}${d}${hh}${mm}${ss}`;
};

const fmtRange = (preset) => {
  // Calculate duration based on preset
  let durationMs = 4 * 60 * 60 * 1000;
  if (preset === '4h') durationMs = 4 * 60 * 60 * 1000;
  if (preset === '24h') durationMs = 24 * 60 * 60 * 1000;
  if (preset === '48h') durationMs = 48 * 60 * 60 * 1000;
  if (preset === '7d') durationMs = 7 * 24 * 60 * 60 * 1000;
  if (preset === '30d') durationMs = 30 * 24 * 60 * 60 * 1000;

  const nowMs = Date.now();
  const startMs = nowMs - durationMs;

  const start = new Date(startMs);
  const now = new Date(nowMs);

  // Filter in UTC
  return `${toStampUTC(start)},${toStampUTC(now)}`;
};

const rxFrame = /(?:[eE][0-9a-fA-F]{23,24}|(?:1b|1B)[0-9a-fA-F]{10}|(?:1b|1B)[0-9a-fA-F]{22})\$?/;

const toISTString = (ts) => {
  if (!ts) return '-';
  const str = String(ts);
  if (str.length < 14) return '-';
  const y = str.slice(0, 4), m = str.slice(4, 6), d = str.slice(6, 8), hh = str.slice(8, 10), mm = str.slice(10, 12), ss = str.slice(12, 14);
  // Input is UTC, convert to Date object
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss)));
  return formatIST(date);
};

const formatIST = (date) => {
  if (!date || isNaN(new Date(date).getTime())) return '-';
  const strFormatted = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(new Date(date));
  const [dmy, hms] = strFormatted.split(', ');
  return `${dmy.replaceAll('/', '-')} | ${hms}`;
};

const stampToUTCDate = (s) => {
  if (!s) return new Date(0);
  const str = String(s);
  if (str.length < 14) return new Date(0);
  const y = str.slice(0, 4), m = str.slice(4, 6), d = str.slice(6, 8), hh = str.slice(8, 10), mm = str.slice(10, 12), ss = str.slice(12, 14);
  return new Date(`${y}-${m}-${d}T${hh}:${mm}:${ss}Z`);
};

export default function DeviceDetails({ imei, onBack }) {
  const [preset, setPreset] = useState('4h');
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const { googleMapsApiKey: key } = useFleetGoogleMaps();
  const apiKey = useMemo(() => (process.env.REACT_APP_API_KEY || (window && window.REACT_APP_API_KEY) || ''), []);

  useEffect(() => {
    const fetchFrames = async () => {
      setLoading(true);
      const range = fmtRange(preset === '4h' ? '4h' : preset);
      const url = `${process.env.REACT_APP_TRACK_API_BASE}/testing3?imeis=${encodeURIComponent(imei)}&TimeRange=${range}`;

      try {
        const res = await fetch(url, { headers: apiKey ? { 'x-api-key': apiKey } : undefined });
        if (res.status === 404) {
          toast.info('No data present between this time range');
          setFrames([]);
          return;
        }

        if (!res.ok) {
          const text = await res.text();
          toast.error(`Server error ${res.status}: ${text.slice(0, 120)}`);
          setFrames([]);
          return;
        }

        let json = {};
        try {
          json = await res.json();
        } catch {
          json = {};
        }

        let payload = json;
        if (typeof payload?.body === 'string') {
          try {
            payload = JSON.parse(payload.body);
          } catch {
            payload = json;
          }
        }

        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        const valid = list.filter(
          (r) => typeof r.packet_data === 'string' && r.packet_data.match(rxFrame)
        );

        const enrichedResults = await Promise.all(
          valid.map(async (r) => {
            const parts = r.packet_data.split(',');
            const epcIndices = [];

            parts.forEach((p, idx) => {
              if (p.match(rxFrame)) {
                epcIndices.push(idx);
              }
            });

            if (epcIndices.length > 1) {
              epcIndices.pop();
            }

            if (epcIndices.length === 0) {
              return [];
            }

            const lat = Number(r.latitude);
            const lng = Number(r.longitude);
            let address = '';

            if (Number.isFinite(lat) && Number.isFinite(lng) && key) {
              try {
                const g = await fetch(
                  `${process.env.REACT_APP_GOOGLE_GEOCODE_API_URL || 'https://maps.googleapis.com/maps/api/geocode/json'}?latlng=${lat},${lng}&key=${key}`
                );
                const gj = await g.json();
                address = gj?.results?.[0]?.formatted_address || '';
              } catch {
                address = '';
              }
            }

            return epcIndices.map((idx) => {
              const id = parts[idx].replace('$', '');
              let rssi = '-';
              if (idx > 0) {
                const before = parts[idx - 1].trim();
                if (before && !isNaN(before) && before.length <= 3) {
                  rssi = before;
                }
              }
              return {
                imei: r.IMEI || imei,
                id,
                ts: r.time_stamp,
                lat,
                lng,
                address,
                rssi,
              };
            });
          })
        );

        const enriched = enrichedResults.flat();
        let finalEnriched = [];

        try {
          const DELATCH_THRESHOLD_MS = 20 * 1000;
          const nowMs = Date.now();
          const epcStats = {};

          const globalSorted = [...enriched].sort(
            (a, b) => stampToUTCDate(b.ts).getTime() - stampToUTCDate(a.ts).getTime()
          );

          const epcGroups = {};
          enriched.forEach((f) => {
            if (!f.id) return;
            if (!epcGroups[f.id]) epcGroups[f.id] = [];
            epcGroups[f.id].push(f);
          });

          Object.keys(epcGroups).forEach((id) => {
            const group = epcGroups[id];
            if (!group || group.length === 0) return;

            const sortedGroup = [...group].sort(
              (a, b) => stampToUTCDate(b.ts).getTime() - stampToUTCDate(a.ts).getTime()
            );

            const latestEPCReadMs = stampToUTCDate(sortedGroup[0].ts).getTime();
            let lastLatchedTime = latestEPCReadMs;
            let lastDelatchedTime = null;

            const startIdx = globalSorted.findIndex(
              (f) => f.id === id && stampToUTCDate(f.ts).getTime() === latestEPCReadMs
            );

            if (startIdx !== -1) {
              for (let i = startIdx; i < globalSorted.length - 1; i += 1) {
                const currentMs = stampToUTCDate(globalSorted[i].ts).getTime();
                const nextMs = stampToUTCDate(globalSorted[i + 1].ts).getTime();
                const diff = currentMs - nextMs;
                if (diff > DELATCH_THRESHOLD_MS) {
                  lastDelatchedTime = nextMs + DELATCH_THRESHOLD_MS;
                  break;
                }
              }
            }

            const status =
              nowMs - latestEPCReadMs > DELATCH_THRESHOLD_MS ? 'DELATCHED' : 'LATCHED';
            epcStats[id] = {
              lastLatchedTime,
              lastDelatchedTime,
              status,
            };
          });

          finalEnriched = enriched.map((f) => {
            if (!f.id || !epcStats[f.id]) return f;
            const stats = epcStats[f.id];
            return {
              ...f,
              latched_ts_raw: stats.lastLatchedTime,
              delatched_ts_raw: stats.lastDelatchedTime,
              status: stats.status,
            };
          });
          finalEnriched.sort((a, b) => String(b.ts).localeCompare(String(a.ts)));
        } catch (e) {
          console.error('Calculation error:', e);
          finalEnriched = enriched;
        }

        setFrames(finalEnriched);
        if (!finalEnriched.length) {
          toast.info('No data present between this time range');
        }
      } catch (err) {
        console.error('fetchFrames error:', err);
        setFrames([]);
        toast.error('Failed to fetch device frames');
      } finally {
        setLoading(false);
      }
    };

    fetchFrames();
  }, [imei, preset, apiKey, key]);

  const filtered = useMemo(() => {
    let out = frames;
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter(f => (
        String(f.imei).toLowerCase().includes(q) ||
        String(f.id || '').toLowerCase().includes(q) ||
        String(f.address || '').toLowerCase().includes(q) ||
        String(f.lat || '').toLowerCase().includes(q) ||
        String(f.lng || '').toLowerCase().includes(q)
      ));
    }
    return out;
  }, [frames, query]);

  return (
    <div className="details-root">
      <header className="details-top">
        <button className="soft" onClick={onBack}>Back</button>
        <div className="spacer" />
        <div className="filters">
          <input className="search" type="text" placeholder="Search ID, IMEI, address…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <FilterDropdown
            value={preset}
            onChange={setPreset}
            options={[
              { value: '4h', label: 'Last 4 Hours' },
              { value: '24h', label: 'Last 24 Hours' },
              { value: '7d', label: 'Last 7 Days' },
              { value: '30d', label: 'Last 30 Days' }
            ]}
          />
        </div>
      </header>
      <main className="details-content">
        {loading ? <div className="loading"><div className="spinner" /></div> : (
          filtered.length === 0 ? (
            <div className="empty">No Valid Frame Data Available.</div>
          ) : (
            filtered.map((f, i) => (
              <div className="card" key={i}>
                <div className="title">Card Details</div>
                <div className="row"><span>IMEI</span><b>{f.imei}</b></div>
                <div className="row"><span>Timestamp</span><b>{toISTString(f.ts)}</b></div>
                <div className="row"><span>EPC</span><b>{f.id}</b></div>
                <div className="row"><span>Latched Last Time</span><b>{formatIST(f.latched_ts_raw)}</b></div>
                <div className="row"><span>Delatched Last Time</span><b>{formatIST(f.delatched_ts_raw)}</b></div>
                <div className="row"><span>RSSI</span><b>{f.rssi}</b></div>
                <div className="row"><span>Serial Number</span><b>-</b></div>
                <div className="row"><span>TA Certification Number</span><b>-</b></div>
                <div className="row"><span>GCW of Trailer</span><b>-</b></div>
                <div className="row"><span>VIN</span><b>-</b></div>
                <div className="row"><span>Axle Count</span><b>-</b></div>
                <div className="row"><span>Registration Number</span><b>-</b></div>
                <div className="row"><span>Insurance Period</span><b>-</b></div>
                <div className="row"><span>Latitude</span><b>{f.lat}</b></div>
                <div className="row"><span>Longitude</span><b>{f.lng}</b></div>
                <div className="row"><span>Address</span><b>{f.address || '-'}</b></div>
              </div>
            ))
          )
        )}
      </main>
    </div>
  );
}
